import React, { useState, useEffect, useCallback } from "react";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import {
  ChevronDownIcon,
  CheckIcon,
  XIcon,
  RefreshCcwIcon,
  Trash2Icon,
} from "lucide-react";
import { UnverifiedUser } from "../../../../types";
import {
  getUnverifiedUsers,
  deleteUnverifiedUser,
} from "../../../../service/apiService";

interface UnverifiedUsersProps {
  onUserChange: () => void;
}

const UnverifiedUsers: React.FC<UnverifiedUsersProps> = ({ onUserChange }) => {
  const [users, setUsers] = useState<UnverifiedUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [userToDelete, setUserToDelete] = useState<UnverifiedUser | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const result = await getUnverifiedUsers();
      setUsers(result);
    } catch (error) {
      console.error("Error fetching unverified users:", error);
      setLoadError("Failed to load unverified users. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDeleteClick = (user: UnverifiedUser) => {
    setDeleteSuccess(null);
    setDeleteError(null);
    setUserToDelete(user);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    const target = userToDelete;
    setUserToDelete(null);
    setDeletingUserId(target.id);
    setDeleteSuccess(null);
    setDeleteError(null);

    try {
      const result = await deleteUnverifiedUser(target.id.toLowerCase());

      if (result && !result.error) {
        setDeleteSuccess(target.id);
        onUserChange();
        await fetchUsers();
      } else {
        setDeleteError(
          result?.error || "Failed to delete user. Please try again."
        );
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;

      if (status === 409) {
        setDeleteError(
          detail ||
            "This user has already signed in and can no longer be deleted here."
        );
        await fetchUsers();
      } else if (status === 404) {
        setDeleteError("User not found. The list may be out of date.");
        await fetchUsers();
      } else if (status === 403) {
        setDeleteError("You don't have permission to delete users.");
      } else if (status === 400) {
        setDeleteError(detail || "You cannot delete your own account.");
      } else {
        setDeleteError(
          detail || err?.message || "Failed to delete user. Please try again."
        );
      }
    } finally {
      setDeletingUserId(null);
    }
  };

  const formatInvitedAt = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "—";

  const daysPendingBadge = (days: number | null) => {
    if (days === null) return null;
    const nearPurge = days >= 10;
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          nearPurge
            ? "bg-red-100 text-red-700"
            : "bg-amber-100 text-amber-700"
        }`}
      >
        {days} {days === 1 ? "day" : "days"} pending
      </span>
    );
  };

  return (
    <>
      <Disclosure as="div" className="py-2">
        <DisclosureButton className="group flex w-full items-center justify-between">
          <span className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
            Unverified Users
          </span>
          <ChevronDownIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-300 group-open:rotate-180" />
        </DisclosureButton>
        <DisclosurePanel className="mt-2 text-sm mx-2 p-4 rounded-lg bg-white">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Invited users who never completed their first sign-in. They are
                deleted automatically 14 days after the invitation; you can
                delete them earlier here.
              </p>
              <button
                type="button"
                onClick={fetchUsers}
                disabled={isLoading}
                className="ml-4 inline-flex items-center rounded-md border border-gray-300
                  bg-white px-3 py-1.5 text-sm font-medium text-gray-700
                  hover:bg-gray-50 focus:outline-none focus:ring-2
                  focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50"
              >
                <RefreshCcwIcon
                  className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
                  aria-hidden="true"
                />
                <span className="ml-2">Refresh</span>
              </button>
            </div>

            {loadError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3">
                <p className="text-sm text-red-700">{loadError}</p>
              </div>
            )}

            {deleteSuccess && (
              <div className="flex items-center">
                <CheckIcon className="h-6 w-6 text-green-500" />
                <span className="ml-2 text-sm text-green-500">
                  {deleteSuccess} permanently deleted
                </span>
              </div>
            )}

            {deleteError && (
              <div className="flex items-center">
                <XIcon className="h-6 w-6 text-violet-500" />
                <span className="ml-2 text-sm text-violet-500">
                  {deleteError}
                </span>
              </div>
            )}

            {isLoading && users.length === 0 ? (
              <div className="flex items-center justify-center py-6">
                <RefreshCcwIcon
                  className="animate-spin h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
                <span className="ml-2 text-sm text-gray-500">
                  Loading unverified users...
                </span>
              </div>
            ) : users.length === 0 ? (
              <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-6 text-center">
                <p className="text-sm text-gray-500">
                  No unverified users. All invited users have signed in.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        User
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Invited
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Pending
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td className="px-4 py-2 text-sm text-gray-900 break-all">
                          {user.id}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap">
                          {formatInvitedAt(user.invited_at)}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          {daysPendingBadge(user.days_pending)}
                        </td>
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => handleDeleteClick(user)}
                            disabled={deletingUserId !== null}
                            className={`inline-flex items-center rounded-md px-3 py-1.5
                              text-sm font-medium text-white
                              ${
                                deletingUserId !== null
                                  ? "bg-gray-400 cursor-not-allowed"
                                  : "bg-red-500 hover:bg-red-600"
                              } focus:outline-none focus:ring-2
                              focus:ring-offset-2 focus:ring-red-500`}
                          >
                            {deletingUserId === user.id ? (
                              <>
                                <RefreshCcwIcon
                                  className="animate-spin mr-2 h-4 w-4 text-white"
                                  aria-hidden="true"
                                />
                                Deleting...
                              </>
                            ) : (
                              <>
                                <Trash2Icon
                                  className="mr-2 h-4 w-4"
                                  aria-hidden="true"
                                />
                                Delete
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DisclosurePanel>
      </Disclosure>

      {/* ── Delete Confirmation Modal ──────────────────────────────────────── */}
      {userToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center scrim px-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-center text-lg font-semibold text-gray-900">
              Delete Unverified User?
            </h3>
            <p className="mt-2 text-center text-sm text-gray-600">
              Are you sure you want to permanently delete{" "}
              <strong className="text-gray-900">{userToDelete.id}</strong>?
              <br />
              <span className="text-gray-500 text-xs mt-1 block">
                This removes the user from both the database and Cognito and
                cannot be undone. The user never signed in, so no sessions or
                data will be lost. You can re-invite them at any time.
              </span>
            </p>
            <div className="mt-6 flex space-x-3">
              <button
                onClick={() => setUserToDelete(null)}
                className="flex-1 rounded-md border border-gray-300 bg-white
                  px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50
                  focus:outline-none focus:ring-2 focus:ring-offset-2
                  focus:ring-violet-500"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="flex-1 rounded-md px-4 py-2 text-sm font-medium
                  text-white bg-red-500 hover:bg-red-600
                  focus:outline-none focus:ring-2 focus:ring-offset-2
                  focus:ring-red-500 transition-colors"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UnverifiedUsers;
